/**
 * パスワード再設定の完了・サーバーの鍵の一覧・まとめて作り直す画面の日本語（en.secretKeys.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。
 * 表記の決め事は ja.ts の冒頭と同じ。文言の正（指示書・Core と合意）は en.secretKeys.ts の冒頭。
 */
import type { enSecretKeys } from './en.secretKeys';
import type { PluralMsg } from './index';

type SecretKeysMessages = { [K in keyof typeof enSecretKeys]: (typeof enSecretKeys)[K] extends string ? string : string | PluralMsg };

export const jaSecretKeys: SecretKeysMessages = {
  'resetDone.title': 'パスワードを変更しました',
  'resetDone.lead': '念のため、あなた以外の人がアカウントに入っていなかったか、ここで確かめてください。',
  'resetDone.removedTitle': '安全のために外したログイン方法',
  'resetDone.removedBody': 'パスワードを変えたので、次のログイン方法を外しました。自分で付けた物なら、ログインしたあと「アカウント」の画面から付け直せます。覚えのない物は付け直さないでください。',
  'resetDone.passkey': 'ログイン用パスキー',
  'resetDone.provider': '{provider}でのログイン',
  'resetDone.added': '{date}に追加',
  'resetDone.lastUsed': '最後に使ったのは{date}',
  'resetDone.neverUsed': 'まだ使われていません',
  'resetDone.keysTitle': '今も使える「サーバーだけで使うキー」（sk_ で始まる）',
  'resetDone.keysBody': 'あなた（またはあなたの AI）が作った覚えのないキーがあれば、他人があなたのアプリを操作できてしまうかもしれません。そのときは下のボタンで、全部を新しいキーに作り直してください。作り直すと古いキーは使えなくなるので、そのあと AI に「新しいキーに入れ替えて」と頼んでください。',
  'resetDone.rotate': 'ログインして、キーを全部作り直す',
  'resetDone.rotateNote': '作り直す前に、もう一度だけあなた本人かを確かめます。',
  'resetDone.login': 'どれも覚えがあるので、このままログインする',

  'sk.col.key': 'キー',
  'sk.col.env': 'アプリ',
  'sk.col.via': '作った方法',
  'sk.col.created': '作った日',
  'sk.col.lastUsed': '最後に使われた日',
  'sk.noLabel': '名前なし',
  'sk.functionsPill': 'Functions',
  'sk.via.dashboard': 'ダッシュボード',
  'sk.via.cli': 'ターミナルのコマンド',
  'sk.via.agent': 'AI',
  'sk.via.functions': '自動（Functions）',
  'sk.via.rotate_all': 'まとめて作り直し',
  'sk.via.unknown': '不明',

  'skList.eyebrow': 'アカウント',
  'skList.title': 'サーバーだけで使うキー（sk_）',
  'skList.lead': 'すべてのプロジェクトで、今も使えるサーバーだけで使うキー（sk_で始まる）の一覧です。キーそのもの（sk_で始まる長い文字列）は作ったときに一度だけ表示されるので、ここでは見られません。1本ずつ使えなくしたり新しく作ったりするのは、それぞれのプロジェクトの「キー（pk_ / sk_）」の画面でできます。',
  'skList.openKeys': 'キー（pk_ / sk_）',
  'skList.empty': '今使えるサーバーだけで使うキー（sk_）はありません。',
  'skList.rotateAll': 'キーを全部作り直す…',
  'skList.rotateHint': 'あなた（またはあなたのAI）が作った覚えのないキーがあれば、他人があなたのアプリを操作できてしまうかもしれません。そのときは全部を作り直してください。',
  'skList.cardTitle': 'サーバーだけで使うキー（sk_）',
  'skList.cardBody': 'すべてのプロジェクトのサーバーだけで使うキー（sk_で始まる）を一覧で見られます。作った覚えのないキーがあれば、全部をまとめて作り直せます。',
  'skList.cardLink': 'キーの一覧を開く',

  'rotate.title': 'サーバーだけで使うキー（sk_）を全部作り直す',
  'rotate.lead': '下のキーを全部使えなくして、同じプロジェクト・同じアプリ（公開中 / テスト用）・同じ名前で新しいキーを作ります。古いキーを入れているサーバーは、新しいキーに入れ替えるまで動きません。元に戻せません。',
  'rotate.functionsRow': 'Functionsの公開し直しが必要',
  'rotate.functionsNote':
    '「Functionsの公開し直しが必要」の行は、Functions（あなたのアプリのサーバーの部分）が自分で持っているキーです。新しいキーはこの画面には出ず、次にFunctionsを公開し直したときに自動で入ります。それまでFunctionsは{product}の機能（ログイン・セーブなど）を使えません。',
  'rotate.confirm': '{count}本のキーを作り直す',
  'rotate.busy': '作り直しています…',
  'rotate.resumed': 'あなた本人だと確かめられました。下の一覧をもう一度見てから、作り直すボタンを押してください。',
  'rotate.changed': 'この一覧を出した後に、キーが増えたか減りました。今の一覧を出し直したので、もう一度見てから作り直してください。',
  'rotate.none': '作り直すキーはありません（今使えるサーバーだけで使うキーがありません）。',
  'rotate.back': 'キーの一覧へ戻る',
  'rotate.doneTitle': '新しいキーを作りました',
  'rotate.doneWarn': '新しいキーは、この画面でしか見られません。離れると二度と表示できません。今コピーして、古いキーを入れていた場所（サーバーの設定）に入れ替えてください。キーそのものはAIとの会話に貼らないでください。',
  'rotate.doneRevoked': '古いキー{count}本を使えなくしました。',
  'rotate.replaces': '{key}の代わり',
  'rotate.redeployTitle': 'Functionsを公開し直してください',
  'rotate.redeployBody': 'Functionsが自分で持っていたキーを使えなくしました。公開し直すまで、そのFunctionsは{product}の機能を使えません。AIに「Functionsを公開し直して」と頼んでください。自分で打つなら、それぞれのプロジェクトのフォルダで次を実行します:',
  'rotate.finish': '新しいキーを保存しました',

  'error.keys_changed': 'この一覧を出した後に、キーが増えたか減りました。一覧をもう一度見てから作り直してください。',
  'error.no_secret_keys': '作り直すキーがありません（今使えるサーバーだけで使うキーがありません）。'
};
